import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Building2, Mail, Lock, Phone, User, Eye, EyeOff, Check, AlertCircle, Sparkles, ArrowLeft } from 'lucide-react';
import { userManagementAPI } from '@/services/api/userManagement';
import PhoneInput from '@/components/ui/PhoneInput';

import { useAppTranslation } from '@/contexts/TranslationContext';
import { config } from '../../config';
import { tvTokens } from '@/src/platform/shared/components/triveny';

/**
 * Sign-Up entry. Triveny renders a frictionless self-serve workspace signup
 * (creates a tenant + admin via /api/platform/auth/signup, then drops the user
 * straight into their new workspace — no forced 2FA). WeaveIn keeps the
 * existing healthcare viewer/sandbox signup. Wrapper has no hooks so the
 * product branch can return before either component's hooks run.
 */
export default function SignupPage() {
  return config.isTriveny ? <TrivenySignup /> : <WeaveInSignup />;
}

/**
 * Persist the whole credential set the platform signup hands back.
 *
 * TRUS-607 (a recurrence of the TRUS-537 "logged out within seconds" class):
 * /api/platform/auth/signup returns a `refresh_token` and `expires_in` alongside
 * the access token (PlatformTokenResponse), but this screen stored ONLY the
 * access token. With no refresh token in localStorage, services/apiClient's
 * recovery path can never run — it gates its refresh attempt on `refresh_token`
 * being present — so the FIRST 401 the brand-new workspace hits is classified
 * unrecoverable and the user is bounced to login. Keep every credential the
 * backend issues, exactly like /login's persistLoginSession.
 *
 * Exported for the regression test.
 */
export function persistSignupSession(data: {
  access_token: string;
  refresh_token?: string;
  expires_in?: number;
  user?: unknown;
}) {
  localStorage.setItem('auth_token', data.access_token);
  if (data.refresh_token) {
    localStorage.setItem('refresh_token', data.refresh_token);
  }
  if (data.expires_in) {
    localStorage.setItem('token_expires_at', (Date.now() + data.expires_in * 1000).toString());
  }
  localStorage.setItem('session_start', Date.now().toString());
  if (data.user) {
    try {
      localStorage.setItem('user', JSON.stringify(data.user));
    } catch {
      /* ignore */
    }
  }
  localStorage.removeItem('activeTab');
}

function TrivenySignup() {
  const [workspace, setWorkspace] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!workspace.trim() || !name.trim() || !email.trim()) {
      setError('Please fill in all fields.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${config.weaveinApi.baseUrl}/api/platform/auth/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          company_name: workspace.trim(),
          admin_email: email.trim(),
          admin_name: name.trim(),
          password,
          plan: 'starter',
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data?.detail || 'Could not create your account. Please try again.');
        return;
      }
      if (data.access_token) {
        // Keep the refresh token + expiry too — dropping them left the new
        // workspace unable to survive its first 401 (TRUS-607 / TRUS-537).
        persistSignupSession(data);
      }
      // Straight into the new workspace — 2FA is optional, never forced.
      window.location.href = '/';
    } catch (err: any) {
      setError(err?.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    'w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[var(--v2-accent)] focus:border-transparent';

  return (
    <div className="triveny-platform min-h-screen bg-[var(--v2-paper,#faf8f4)] flex flex-col">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <a href="https://triveny.ai" className="flex items-center" aria-label="Triveny home">
            <span style={{ fontFamily: tvTokens.fontDisplay, fontSize: 22, fontWeight: 600, letterSpacing: '-0.02em', color: tvTokens.ink }}>
              Triveny
            </span>
          </a>
          <a href="/login" className="text-sm text-gray-600 hover:text-gray-900">
            Log in
          </a>
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-md">
          <div className="text-center mb-6">
            <h1 style={{ fontFamily: tvTokens.fontDisplay, fontSize: 30, fontWeight: 700, letterSpacing: '-0.02em', color: tvTokens.ink }}>
              Create your workspace
            </h1>
            <p className="text-sm text-gray-500 mt-2">
              Start free. Use it as your personal CRM, then invite your team when you’re ready.
            </p>
          </div>

          <form onSubmit={submit} className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 space-y-4">
            {error && (
              <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Workspace name</label>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input className={inputCls + ' pl-9'} value={workspace} onChange={(e) => setWorkspace(e.target.value)} placeholder="Acme Co (or your name)" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Your name</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input className={inputCls + ' pl-9'} value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Doe" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Work email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input className={inputCls + ' pl-9'} type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="jane@acme.com" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input className={inputCls + ' pl-9 pr-9'} type={showPw ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 8 characters" />
                <button type="button" onClick={() => setShowPw((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="w-full py-2.5 rounded-lg text-white text-sm font-semibold disabled:opacity-50"
              style={{ background: tvTokens.ink }}
            >
              {submitting ? 'Creating workspace…' : 'Create workspace'}
            </button>
            <p className="text-center text-xs text-gray-400">
              By creating a workspace you agree to the Terms &amp; Privacy.
            </p>
          </form>

          <p className="text-center text-sm text-gray-500 mt-4">
            Already have an account?{' '}
            <a href="/login" className="font-medium" style={{ color: tvTokens.ink }}>
              Log in
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}

function WeaveInSignup() {
  const { t } = useAppTranslation();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organization_name: '',
    phone_number: '',
    password: '',
    confirmPassword: '',
    terms_accepted: false,
    hipaa_accepted: false,
    request_sandbox: false,
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getPasswordStrength = (password: string): { score: number; label: string; color: string } => {
    let score = 0;
    if (password.length >= 8) score++;
    if (password.length >= 12) score++;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
    if (/\d/.test(password)) score++;
    if (/[^a-zA-Z\d]/.test(password)) score++;

    if (score <= 1) return { score, label: 'Weak', color: '#dc2626' };
    if (score <= 3) return { score, label: 'Fair', color: '#ea580c' };
    if (score <= 4) return { score, label: 'Good', color: '#ca8a04' };
    return { score, label: 'Strong', color: '#16a34a' };
  };

  const passwordStrength = getPasswordStrength(formData.password);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validations
    if (!formData.name.trim()) {
      setError('Name is required');
      return;
    }

    // Basic email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email || !emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      return;
    }

    if (!formData.phone_number.trim()) {
      setError('Phone number is required for account security');
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!formData.terms_accepted) {
      setError('You must accept the Terms of Service to continue');
      return;
    }

    if (!formData.hipaa_accepted) {
      setError('You must accept the HIPAA Agreement to continue');
      return;
    }

    try {
      setSubmitting(true);
      const response = await userManagementAPI.selfServiceSignup({
        name: formData.name,
        email: formData.email,
        organization_name: formData.organization_name,
        phone_number: formData.phone_number,
        password: formData.password,
        terms_accepted: formData.terms_accepted,
        hipaa_accepted: formData.hipaa_accepted,
        request_sandbox: formData.request_sandbox,
      });

      // Store auth token
      localStorage.setItem('auth_token', response.auth_token);
      localStorage.setItem('active_cbo_id', response.organization_id.toString());

      // Redirect based on account type
      if (response.sandbox_provisioned) {
        navigate('/');  // Full dashboard for sandbox users
      } else {
        navigate('/viewer/capabilities');
      }
    } catch (err: any) {
      console.error('Signup error:', err);
      // The API answers 409 with a structured detail ({code, message}) when the
      // person's organization is already on WeaveIn (TRUS-654); other errors
      // are plain strings.
      const detail = err.response?.data?.detail;
      const message =
        typeof detail === 'string'
          ? detail
          : detail && typeof detail.message === 'string'
            ? detail.message
            : 'Failed to create account. Please try again.';
      setError(message);
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-blue-50 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        {/* Back to Login */}
        <Link
          to="/login"
          className="inline-flex items-center gap-2 text-gray-600 hover:text-teal-600 mb-6 transition-colors text-sm font-medium"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Login</span>
        </Link>

        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-teal-500 to-blue-500 rounded-2xl mb-4">
            <Sparkles className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {t('signup.start_your_care_coordination_journey', 'Start Your Care Coordination Journey')}
          </h1>
          <p className="text-gray-600 dark:text-gray-300">
            {t('signup.create_your_organization_account_and', 'Create your organization account and explore Trust Marketplace')}
          </p>
        </div>

        {/* Form Card */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 border border-gray-100">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Error Alert */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
                <AlertCircle className="w-5 h-5 text-red-600 mr-3 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            {/* Name */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                {t('signup.full_name', 'Full Name *')}
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  id="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  placeholder={t('signup.john_doe', 'John Doe')}
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                {t('signup.email', 'Email *')}
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  id="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  placeholder="john@example.com"
                />
              </div>
            </div>

            {/* Organization Name */}
            <div>
              <label htmlFor="organization_name" className="block text-sm font-medium text-gray-700 mb-2">
                {t('signup.organization_name_optional', 'Organization Name (optional)')}
              </label>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  id="organization_name"
                  type="text"
                  value={formData.organization_name}
                  onChange={(e) => setFormData({ ...formData, organization_name: e.target.value })}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  placeholder={t('signup.acme_health_services', 'Acme Health Services')}
                />
              </div>
            </div>

            {/* Phone Number */}
            <div>
              <label htmlFor="phone_number" className="block text-sm font-medium text-gray-700 mb-2">
                {t('signup.phone_number', 'Phone Number *')}
              </label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <PhoneInput
                  id="phone_number"
                  required
                  value={formData.phone_number}
                  onChange={(v) => setFormData({ ...formData, phone_number: v })}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  placeholder="+1 (555) 123-4567"
                />
              </div>
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                {t('signup.required_for_account_security_and', 'Required for account security and two-factor authentication')}
              </p>
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                {t('signup.password', 'Password *')}
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {formData.password && (
                <div className="mt-2">
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="text-gray-600 dark:text-gray-300">{t('signup.password_strength', 'Password strength:')}</span>
                    <span style={{ color: passwordStrength.color }} className="font-medium">
                      {passwordStrength.label}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      style={{
                        width: `${(passwordStrength.score / 5) * 100}%`,
                        backgroundColor: passwordStrength.color,
                      }}
                      className="h-2 rounded-full transition-all duration-300"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Confirm Password */}
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                {t('signup.confirm_password', 'Confirm Password *')}
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  required
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Terms & HIPAA Checkboxes */}
            <div className="space-y-3 pt-4">
              <div className="flex items-start">
                <input
                  id="terms_accepted"
                  type="checkbox"
                  checked={formData.terms_accepted}
                  onChange={(e) => setFormData({ ...formData, terms_accepted: e.target.checked })}
                  className="mt-1 h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
                />
                <label htmlFor="terms_accepted" className="ml-3 text-sm text-gray-700 dark:text-gray-300">
                  I accept the{' '}
                  <a href="/consent/terms-of-use" target="_blank" className="text-teal-600 hover:text-teal-700 underline">
                    {t('signup.terms_of_service', 'Terms of Service')}
                  </a>{' '}
                  *
                </label>
              </div>

              <div className="flex items-start">
                <input
                  id="hipaa_accepted"
                  type="checkbox"
                  checked={formData.hipaa_accepted}
                  onChange={(e) => setFormData({ ...formData, hipaa_accepted: e.target.checked })}
                  className="mt-1 h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
                />
                <label htmlFor="hipaa_accepted" className="ml-3 text-sm text-gray-700 dark:text-gray-300">
                  I accept the{' '}
                  <a href="/consent/hipaa-authorization" target="_blank" className="text-teal-600 hover:text-teal-700 underline">
                    {t('signup.hipaa_authorization', 'HIPAA Authorization')}
                  </a>{' '}
                  *
                </label>
              </div>
            </div>

            {/* Account Type Selection */}
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 space-y-3">
              <p className="text-sm font-medium text-gray-700 dark:text-gray-200">Choose your account type:</p>
              <label className="flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors
                ${!formData.request_sandbox ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/20' : 'border-gray-200 dark:border-gray-600 hover:border-gray-300'}">
                <input
                  type="radio"
                  name="account_type"
                  checked={!formData.request_sandbox}
                  onChange={() => setFormData({ ...formData, request_sandbox: false })}
                  className="mt-1"
                />
                <div>
                  <span className="font-medium text-gray-900 dark:text-white">Viewer Account</span>
                  <p className="text-xs text-gray-500 mt-0.5">Browse the platform, view capabilities, schedule a demo</p>
                </div>
              </label>
              <label className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors
                ${formData.request_sandbox ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20' : 'border-gray-200 dark:border-gray-600 hover:border-gray-300'}`}>
                <input
                  type="radio"
                  name="account_type"
                  checked={formData.request_sandbox}
                  onChange={() => setFormData({ ...formData, request_sandbox: true })}
                  className="mt-1"
                />
                <div>
                  <span className="font-medium text-gray-900 dark:text-white">Sandbox Account</span>
                  <span className="ml-2 text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-medium">Recommended</span>
                  <p className="text-xs text-gray-500 mt-0.5">Full platform access with 50 demo members, 3 programs, forms, workflows, and API access (30-day trial)</p>
                </div>
              </label>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-gradient-to-r from-teal-600 to-blue-600 text-white font-semibold py-3 px-6 rounded-lg hover:from-teal-700 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg"
            >
              {submitting ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {t('signup.creating_account', 'Creating Account...')}
                </span>
              ) : (
                'Create Account'
              )}
            </button>
          </form>

          {/* Footer */}
          <div className="mt-6 text-center text-sm text-gray-600 dark:text-gray-300">
            Already have an account?{' '}
            <Link to="/login" className="text-teal-600 hover:text-teal-700 font-medium">
              {t('signup.sign_in', 'Sign in')}
            </Link>
          </div>
        </div>

        {/* Info Card */}
        <div className={`mt-6 ${formData.request_sandbox ? 'bg-amber-50 border-amber-200' : 'bg-teal-50 border-teal-200'} border rounded-xl p-4`}>
          <p className={`text-sm ${formData.request_sandbox ? 'text-amber-800' : 'text-teal-800'} flex items-start`}>
            <Check className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
            <span>
              {formData.request_sandbox ? (
                <>Your organization will be created as a <strong>sandbox environment</strong> with sample data, full admin access, and API credentials. The sandbox expires after 30 days.</>
              ) : (
                <>{t('signup.your_organization_will_be_created', 'Your organization will be created with a')} <strong>viewer account</strong>. Schedule a demo to unlock full platform access.</>
              )}
            </span>
          </p>
        </div>
      </div>
    </div>
  );
}
